import { isDictsysFormat, isGyldendalFormat } from "@/helpers/article-format.helper";

import Dictionaries from "./dictionaries";
import { getNextLevel } from "./level";

export const SEARCH_DEBOUNCE_TIME = 200;

const TYPE_EXACT = "exact";
const TYPE_FUZZY = "fuzzy";
const TYPE_EMPTY = "empty";

/**
 * @param {Array<object>} articles
 * @param {string} lemmaId
 * @param {string} inflectionSchemeId
 * @param {import('@/types/search').ArticleTab} tab
 *
 * @returns {import("@/types/article-formats/dictsys").DictsysArticle|object|null}
 */
function getInflectionSchemeDataFromDictsysFormat(articles, lemmaId, inflectionSchemeId, tab) {
  let article;

  for (const a of articles) {
    if (a.id === lemmaId) {
      article = a;
      break;
    }

    const alternativeSpelling = a.alternativeSpellings.find((s) => s.id === lemmaId);
    if (alternativeSpelling !== undefined) {
      article = alternativeSpelling;
      break;
    }
  }

  if (article === undefined) {
    return null;
  }

  if (inflectionSchemeId === "") {
    return article;
  }

  let articleContentElements = [];
  if (tab === "translations") {
    articleContentElements = article.translations || [];
  } else if (tab === "combinations") {
    articleContentElements = article.combinations || [];
  }

  const elementWithInflectionScheme = articleContentElements.find(
    (element) => element.inflectionScheme.id === inflectionSchemeId
  );

  if (elementWithInflectionScheme === undefined) {
    return null;
  }

  return { ...elementWithInflectionScheme, headWord: elementWithInflectionScheme.translation };
}

/**
 * @param {Array<import('@/types/article-formats/gyldendal').GyldendalArticle>} articles
 * @param {number} articleId
 * @param {number} translationLemma
 *
 * @returns {(import('@/types/article-formats/gyldendal').GyldendalLemma & { lanuage: string; }) | null}
 */
function getInflectionSchemeDataFromGyldendalFormat(articles, articleId, translationLemma) {
  const article = getGyldendalArticle(articles, articleId);
  if (article === null) {
    return null;
  }

  const sourceLanguage = article.methodId.slice(-4).substring(0, 2);
  const targetLanguage = article.methodId.slice(-2);

  let lemma = article.lemma;
  lemma.language = sourceLanguage;

  if (translationLemma) {
    const translationLemmas = getGyldendalTranslationLemmas(article);
    lemma = translationLemmas[translationLemma];
    lemma.language = targetLanguage;
  }

  if (lemma === undefined) {
    return null;
  }

  return lemma;
}

/**
 * @param {Array<import('@/types/article-formats/gyldendal').GyldendalArticle>} articles
 * @param {number} articleId
 *
 * @returns {(import('@/types/article-formats/gyldendal').GyldendalArticle) | null}
 */
function getGyldendalArticle(articles, articleId) {
  for (const article of articles) {
    if (article.id === articleId) {
      return article;
    }

    for (const phrase of article.phrases) {
      if (phrase.id === articleId) {
        return phrase;
      }
    }
  }

  return null;
}

/**
 * @param {import('@/types/article-formats/gyldendal').GyldendalArticle} article
 *
 * @returns {Record<number, import('@/types/article-formats/gyldendal').GyldendalLemma>}
 */
function getGyldendalTranslationLemmas(article) {
  const lemmas = {};
  for (const meaning of article.meanings) {
    for (const subMeaning of meaning.subMeanings) {
      for (const translationGroup of subMeaning.translationGroups) {
        for (const translation of translationGroup.translations) {
          if (translation.lemma) {
            lemmas[translation.lemma.id] = translation.lemma;
          }
        }
      }
    }
  }

  return lemmas;
}

const Search = {
  /**
   * @param {string} inflectionSchemeId
   * @param {import('@/types/search').SearchResult} searchResult
   * @param {import('@/types/search').ArticleTab} tab
   *
   * @returns {import('@/types/inflection-scheme-data.type').InflectionSchemeData|null}
   */
  getInflectionSchemeData(inflectionSchemeId, searchResult, tab) {
    const [idPart1, idPart2] = inflectionSchemeId.split("_");

    if (searchResult.articles.length === 0) {
      return null;
    }

    if (isGyldendalFormat(searchResult.methodId)) {
      return getInflectionSchemeDataFromGyldendalFormat(
        searchResult.articles,
        parseInt(idPart1),
        parseInt(idPart2)
      );
    }

    if (isDictsysFormat(searchResult.methodId)) {
      return getInflectionSchemeDataFromDictsysFormat(searchResult.articles, idPart1, idPart2, tab);
    }

    return null;
  },

  /**
   * @typedef {{
   *  direction: string,
   *  level: string
   * }} SearchResultInfo
   *
   * @param {import('@/types/search').SearchResponse} searchResult
   * @param {string} languageGroup
   * @param {string} direction
   * @param {string} level
   *
   * @returns {SearchResultInfo|null}
   */
  getSearchResultInfo(searchResult, languageGroup, direction, level) {
    let directionWithResult = "";

    while (level !== null) {
      const resultMethodIds =
        this.getMethodIdsWithResultFromSearchResultForLanguageGroupAndDirectionAndLevel(
          searchResult,
          languageGroup,
          direction,
          level
        );

      if (resultMethodIds.length > 0) {
        directionWithResult = direction;
        break;
      }

      const reverseDirection = Dictionaries.getReverseDirectionByDirectionAndLevel(
        direction,
        level
      );

      // Monolingual won't have a reverse direction
      if (reverseDirection !== undefined) {
        const resultReverseMethodIds =
          this.getMethodIdsWithResultFromSearchResultForLanguageGroupAndDirectionAndLevel(
            searchResult,
            languageGroup,
            reverseDirection,
            level
          );

        if (resultReverseMethodIds.length > 0) {
          directionWithResult = reverseDirection;
          break;
        }
      }

      level = getNextLevel(level);
    }

    if (level === null || directionWithResult === "") {
      return null;
    }

    return {
      direction: directionWithResult,
      level
    };
  },

  /**
   * @param {import('@/types/search').SearchResponse} searchResult
   * @param {string} languageGroup
   * @param {string} direction
   * @param {string} level
   *
   * @returns {string[]}
   */
  getMethodIdsWithResultFromSearchResultForLanguageGroupAndDirectionAndLevel(
    searchResult,
    languageGroup,
    direction,
    level
  ) {
    return Dictionaries.getMethodIdsByLanguageGroupAndDirectionAndLevel(
      languageGroup,
      direction,
      level
    ).filter(
      (methodId) => searchResult.result.find((r1) => r1.methodId === methodId)?.articles.length > 0
    );
  },

  /**
   * @param {object} searchResult
   *
   * @returns {boolean}
   */
  searchResultIsEmpty(searchResult) {
    return !searchResult || searchResult.type === undefined || searchResult.type === TYPE_EMPTY;
  },

  /**
   * @param {object} searchResult
   *
   * @returns {boolean}
   */
  searchResultIsExactMatch(searchResult) {
    return searchResult?.type === TYPE_EXACT;
  },

  /**
   * @param {object} searchResult
   *
   * @returns {boolean}
   */
  searchResultIsFuzzy(searchResult) {
    return searchResult?.type === TYPE_FUZZY;
  }
};

export default Search;
